/* ZIITOP: explicit purchase actions. The existing gallery and native add-to-cart
   form remain the source of product/variant selection. No cart clearing. */
(function (global) {
  'use strict';

  function amazonUrl(value) {
    if (typeof value !== 'string') return '';
    const raw = value.trim();
    if (!raw || /[\u0000-\u001f\u007f]/.test(raw)) return '';
    try {
      const url = new URL(raw);
      const host = url.hostname.toLowerCase();
      const amazon = /(^|\.)amazon\.(com|ca|co\.uk|com\.au|de|fr|it|es|co\.jp|in|com\.mx|com\.br|nl|se|pl|com\.be|ie|sg|ae|sa|eg|com\.tr|co\.za)$/.test(host);
      if (url.protocol !== 'https:' || url.username || url.password || url.port || (!amazon && host !== 'amzn.to')) return '';
      return raw; // Preserve Amazon Attribution and affiliate parameters exactly.
    } catch (_) { return ''; }
  }

  function resolveAmazon(variant, productUrl) {
    const exact = amazonUrl(variant?.amazon_url);
    if (exact) return { url: exact, scope: 'variant' };
    const fallback = amazonUrl(productUrl);
    return { url: fallback, scope: fallback ? 'product' : 'none' };
  }

  function checkoutUrl(cartRoute, variantId, quantity, origin) {
    const id = String(variantId);
    const count = Number(quantity);
    if (!/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(count) || count < 1) {
      throw new Error('Choose an available option and a valid quantity.');
    }
    const url = new URL(cartRoute || '/cart', origin);
    if (url.origin !== origin || !/\/cart\/?$/.test(url.pathname)) {
      throw new Error('Invalid store cart route.');
    }
    url.pathname = url.pathname.replace(/\/$/, '') + '/' + id + ':' + count;
    url.search = '';
    url.hash = '';
    return url.href;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { amazonUrl, resolveAmazon, checkoutUrl };
  }
  if (!global.document || global.ZIITOPPurchaseLoaded) return;
  global.ZIITOPPurchaseLoaded = true;

  function init(root) {
    if (root.dataset.purchaseReady) return;
    const form = root.querySelector('.product-form');
    const json = root.querySelector('[data-product-json]');
    if (!form || !json) return;
    let product;
    try { product = JSON.parse(json.textContent); } catch (_) { return; }
    if (!Array.isArray(product.variants)) return;
    root.dataset.purchaseReady = 'true';

    const buy = root.querySelector('[data-buy-now]');
    const buyLabel = root.querySelector('[data-buy-label]');
    const add = root.querySelector('[data-add-to-cart]');
    const mobile = root.querySelector('[data-mobile-atc-button]');
    const amazon = root.querySelector('[data-amazon-link]');
    const amazonWrap = root.querySelector('[data-amazon-wrap]');
    const amazonNote = root.querySelector('[data-amazon-note]');
    const errorBox = root.querySelector('[data-purchase-error]');
    const variantSelect = root.querySelector('[data-variant-select]');
    const qty = form.querySelector('[name="quantity"]');
    const groups = Array.from(root.querySelectorAll('[data-option-group]'));
    let busy = false;
    let resetTimer;

    function currentVariant() {
      // Do not trust a stale hidden ID after a shopper changes a color or size.
      if (groups.length) {
        const selected = groups.map(group => group.querySelector('[data-option-input]:checked')?.value);
        return product.variants.find(v => v.options?.length === selected.length && v.options.every((value, i) => value === selected[i])) || null;
      }
      return product.variants.find(v => String(v.id) === String(variantSelect?.value)) || null;
    }

    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    }

    function sync() {
      const variant = currentVariant();
      if (buy) {
        buy.disabled = busy || !variant?.available;
        if (buyLabel) buyLabel.textContent = busy ? (buy.dataset.loadingLabel || 'OPENING CHECKOUT…') :
          (!variant ? 'UNAVAILABLE' : !variant.available ? 'SOLD OUT' : (buy.dataset.label || 'BUY NOW'));
      }
      if (amazon && amazonWrap) {
        // Shopify stock does not indicate Amazon stock. Keep a valid Amazon
        // link usable for a sold-out Shopify variant; never invent availability.
        const result = variant ? resolveAmazon(variant, product.amazon_url) : { url: '', scope: 'none' };
        amazonWrap.hidden = !result.url;
        if (result.url) {
          amazon.href = result.url;
          amazon.dataset.linkScope = result.scope;
        } else {
          amazon.removeAttribute('href');
          delete amazon.dataset.linkScope;
        }
        if (amazonNote) {
          amazonNote.textContent = result.scope === 'variant' ?
            'Opens Amazon in a new tab. Confirm your options and quantity there. Prices and availability may differ.' :
            'Opens Amazon in a new tab. Choose your color, size and quantity there. Prices and availability may differ.';
        }
      }
    }

    function reset() {
      busy = false;
      clearTimeout(resetTimer);
      form.removeAttribute('aria-busy');
      const variant = currentVariant();
      if (add) add.disabled = !variant?.available;
      if (mobile) mobile.disabled = !variant?.available;
      sync();
    }
    root.addEventListener('ziitop:variant-change', () => { showError(''); sync(); });
    form.addEventListener('change', () => { showError(''); sync(); });
    global.addEventListener('pageshow', reset);

    form.addEventListener('submit', event => {
      if (event.defaultPrevented) return; // Respect app validation.
      if (busy) { event.preventDefault(); return; }
      const variant = currentVariant();
      const quantity = Number(qty?.value || (qty ? '' : 1));
      if (!variant?.available || !Number.isSafeInteger(quantity) || quantity < 1) {
        event.preventDefault();
        showError(!variant?.available ? 'Please choose an available color and size.' : 'Please enter a whole-number quantity of at least 1.');
        return;
      }
      if (variantSelect) variantSelect.value = String(variant.id);
      if (!form.reportValidity()) { event.preventDefault(); return; }

      // Native submit (including the existing mobile sticky Add to Cart)
      // retains Shopify's normal add-to-cart behavior and existing cart items.
      if (!event.submitter?.matches('[data-buy-now]')) return;

      // In no-JS mode the button's name=return_to posts the selected item and
      // sends the customer into the normal checkout. With JS, use Shopify's
      // documented selected-item cart permalink for direct checkout.
      event.preventDefault();
      try {
        const url = checkoutUrl(global.ZIITOP?.routes?.cart || '/cart', variant.id, quantity, global.location.origin);
        if (global.Shopify?.designMode) {
          showError('Open the storefront preview in a new tab to test checkout.');
          return;
        }
        busy = true;
        form.setAttribute('aria-busy', 'true');
        if (add) add.disabled = true;
        if (mobile) mobile.disabled = true;
        sync();
        // Recover controls if navigation fails or the browser returns via BFCache.
        resetTimer = global.setTimeout(reset, 10000);
        global.location.assign(url);
      } catch (error) {
        reset();
        showError(error.message || 'Checkout could not be opened. Please try again.');
      }
    });

    if (amazon) amazon.addEventListener('click', event => {
      const variant = currentVariant();
      const result = variant ? resolveAmazon(variant, product.amazon_url) : { url: '' };
      if (!result.url) { event.preventDefault(); sync(); return; }
      amazon.href = result.url;
      // Hook only. This does not install analytics or report an Amazon purchase.
      root.dispatchEvent(new CustomEvent('ziitop:amazon-click', { bubbles: true, detail: {
        productId: product.id, variantId: variant.id, url: result.url, linkScope: result.scope
      }}));
    });
    sync();
  }

  function initAll(context) {
    if (context?.matches?.('[data-product-root]')) init(context);
    (context || document).querySelectorAll('[data-product-root]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})(typeof window !== 'undefined' ? window : globalThis);
